(function () {
  const LOG_PREFIX = '[PDP Zoom]';
  const DESKTOP_QUERY = '(min-width: 992px)';
  const PANE_SIZE = 520;
  const PANE_GUTTER = 42;
  const ZOOM_BOOST = 1.45;
  const MOVE_LOG_INTERVAL = 120;
  const controllers = new Map();
  let variantSubscriptionBound = false;

  function log(message, payload) {
    if (payload !== undefined) {
      console.log(`${LOG_PREFIX} ${message}`, payload);
      return;
    }

    console.log(`${LOG_PREFIX} ${message}`);
  }

  function warn(message, payload) {
    if (payload !== undefined) {
      console.warn(`${LOG_PREFIX} ${message}`, payload);
      return;
    }

    console.warn(`${LOG_PREFIX} ${message}`);
  }

  class PdpZoomController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || 'unknown';
      this.mainArea = root.querySelector('[data-zoom-wrapper]');
      this.mainImage = root.querySelector('[data-pdp-main-image]');
      this.lens = root.querySelector('.pdp-zoom-lens');
      this.pane = root.querySelector('.pdp-zoom-pane')
        || document.querySelector(`.pdp-zoom-pane[data-zoom-pane-for="${this.sectionId}"]`)
        || this.createPaneElement();
      this.thumbContainer = root.querySelector('[data-pdp-thumbs]');
      this.thumbPrevButton = root.querySelector('[data-pdp-thumbs-prev]');
      this.thumbNextButton = root.querySelector('[data-pdp-thumbs-next]');
      this.mediaGallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      this.desktopMql = window.matchMedia(DESKTOP_QUERY);
      this.zoomImage = new Image();
      this.zoomImageReady = false;
      this.currentZoomSrc = '';
      this.lastPointerEvent = null;
      this.lastMoveLogAt = 0;
      this.isThumbDragging = false;
      this.thumbDragMoved = false;
      this.thumbDragStartX = 0;
      this.thumbDragStartScrollLeft = 0;
      this.attributeObserver = null;
      this.galleryObserver = null;

      log(`Initializing gallery ${this.sectionId}`);
      log('Element selection', {
        root: !!this.root,
        mainArea: !!this.mainArea,
        mainImage: !!this.mainImage,
        lens: !!this.lens,
        pane: !!this.pane,
        thumbContainer: !!this.thumbContainer,
        mediaGallery: !!this.mediaGallery,
      });

      if (!this.mainArea || !this.mainImage || !this.lens || !this.pane) {
        warn(`Initialization aborted for gallery ${this.sectionId}: required elements missing.`);
        return;
      }

      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleMainImageLoad = this.handleMainImageLoad.bind(this);
      this.handleThumbClick = this.handleThumbClick.bind(this);
      this.handleThumbPrevClick = this.handleThumbPrevClick.bind(this);
      this.handleThumbNextClick = this.handleThumbNextClick.bind(this);
      this.handleThumbScroll = this.handleThumbScroll.bind(this);
      this.handleThumbDragStart = this.handleThumbDragStart.bind(this);
      this.handleThumbDragMove = this.handleThumbDragMove.bind(this);
      this.handleThumbDragEnd = this.handleThumbDragEnd.bind(this);
      this.handleThumbNativeDragStart = this.handleThumbNativeDragStart.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleGalleryMutation = this.handleGalleryMutation.bind(this);

      this.preparePane();
      this.attachEvents();
      this.observeChanges();
      this.setZoomSource();
      this.resetZoom('initial state');
    }

    createPaneElement() {
      log(`Creating missing zoom pane for gallery ${this.sectionId}`);
      const pane = document.createElement('div');
      pane.className = 'pdp-zoom-pane';
      pane.setAttribute('aria-hidden', 'true');
      return pane;
    }

    preparePane() {
      this.pane.dataset.zoomPaneFor = this.sectionId;
      this.pane.setAttribute('data-pane-placement', 'right');
      this.pane.style.setProperty('width', `${PANE_SIZE}px`, 'important');
      this.pane.style.setProperty('height', `${PANE_SIZE}px`, 'important');
      this.pane.style.setProperty('position', 'fixed', 'important');
      this.pane.style.setProperty('z-index', '1000', 'important');
      this.pane.style.setProperty('pointer-events', 'none', 'important');

      if (this.pane.parentElement !== document.body) {
        document.body.appendChild(this.pane);
        log(`Zoom pane moved outside gallery main area for section ${this.sectionId}`);
      }
    }

    attachEvents() {
      log(`Attaching events for gallery ${this.sectionId}`);
      this.mainArea.addEventListener('mouseenter', this.handleMouseEnter);
      this.mainArea.addEventListener('mousemove', this.handleMouseMove);
      this.mainArea.addEventListener('mouseleave', this.handleMouseLeave);
      this.mainImage.addEventListener('load', this.handleMainImageLoad);
      this.root.addEventListener('click', this.handleThumbClick);
      this.thumbPrevButton?.addEventListener('click', this.handleThumbPrevClick);
      this.thumbNextButton?.addEventListener('click', this.handleThumbNextClick);
      this.thumbContainer?.addEventListener('scroll', this.handleThumbScroll, { passive: true });
      this.thumbContainer?.addEventListener('mousedown', this.handleThumbDragStart);
      this.thumbContainer?.addEventListener('dragstart', this.handleThumbNativeDragStart);

      if (typeof this.desktopMql.addEventListener === 'function') {
        this.desktopMql.addEventListener('change', this.handleViewportChange);
      } else if (typeof this.desktopMql.addListener === 'function') {
        this.desktopMql.addListener(this.handleViewportChange);
      }

      log(`Event attachment complete for gallery ${this.sectionId}`);
      this.updateThumbNavState();
    }

    observeChanges() {
      this.attributeObserver = new MutationObserver((mutations) => {
        const shouldRefresh = mutations.some((mutation) => {
          return mutation.type === 'attributes'
            && (mutation.attributeName === 'src' || mutation.attributeName === 'data-zoom-src');
        });

        if (!shouldRefresh) return;

        log(`Main image attributes changed for gallery ${this.sectionId}`, mutations);
        this.setZoomSource();
        this.resetZoom('main image attribute mutation');
      });

      this.attributeObserver.observe(this.mainImage, {
        attributes: true,
        attributeFilter: ['src', 'data-zoom-src'],
      });

      this.galleryObserver = new MutationObserver(this.handleGalleryMutation);
      this.galleryObserver.observe(this.root, {
        childList: true,
        subtree: true,
      });

      log(`Mutation observers attached for gallery ${this.sectionId}`);
    }

    handleGalleryMutation(mutations) {
      const latestMainImage = this.root.querySelector('[data-pdp-main-image]');
      if (latestMainImage && latestMainImage !== this.mainImage) {
        warn(`Main image element was replaced in gallery ${this.sectionId}; reinitializing zoom controller.`);
        initGallery(this.root);
        return;
      }

      const relevantMutation = mutations.some((mutation) => {
        if (mutation.target === this.thumbContainer) return true;

        return Array.from(mutation.addedNodes).some((node) => {
          return node.nodeType === Node.ELEMENT_NODE && node.matches?.('[data-pdp-thumb-btn], .pdp-thumb-btn, img');
        });
      });

      if (!relevantMutation) return;

      log(`Gallery subtree updated for gallery ${this.sectionId}`, mutations);
      this.updateThumbNavState();
    }

    handleViewportChange() {
      log(`Viewport change detected for gallery ${this.sectionId}`, { matches: this.desktopMql.matches });
      if (!this.desktopMql.matches) {
        this.resetZoom('viewport switched to mobile/tablet');
      }

      this.updateThumbNavState();
    }

    handleMainImageLoad() {
      log(`Main image loaded for gallery ${this.sectionId}`, {
        src: this.mainImage.currentSrc || this.mainImage.src,
        naturalWidth: this.mainImage.naturalWidth,
        naturalHeight: this.mainImage.naturalHeight,
      });
      this.setZoomSource();
      this.resetZoom('main image load');
    }

    handleThumbClick(event) {
      if (this.thumbDragMoved) {
        event.preventDefault();
        event.stopPropagation();
        this.thumbDragMoved = false;
        return;
      }

      const button = event.target.closest('[data-pdp-thumb-btn]');
      if (!button || !this.root.contains(button)) return;

      const mediaId = button.dataset.mediaId || '';
      const imageSrc = button.dataset.imageSrc || '';
      const zoomSrc = button.dataset.zoomSrc || '';
      const imageAlt = button.dataset.imageAlt || '';

      log(`Thumbnail clicked in gallery ${this.sectionId}`, {
        mediaId,
        imageSrc,
        zoomSrc,
      });

      this.root.querySelectorAll('[data-pdp-thumb-btn].active').forEach((thumb) => thumb.classList.remove('active'));
      button.classList.add('active');

      if (!imageSrc) {
        warn(`Thumbnail ${mediaId || '(unknown)'} has no image source; zoom pane will not update from thumbnail click.`);
        this.syncMediaGallery(mediaId);
        this.resetZoom('non-image thumbnail click');
        return;
      }

      if (this.mainImage.getAttribute('src') !== imageSrc) {
        this.mainImage.setAttribute('src', imageSrc);
      }
      this.mainImage.setAttribute('data-zoom-src', zoomSrc || imageSrc);
      this.mainImage.setAttribute('alt', imageAlt);
      this.syncMediaGallery(mediaId);
      this.setZoomSource();
      this.resetZoom('thumbnail click');
    }

    handleThumbPrevClick() {
      this.scrollThumbsBy(-1);
    }

    handleThumbNextClick() {
      this.scrollThumbsBy(1);
    }

    handleThumbScroll() {
      this.updateThumbNavState();
    }

    handleThumbDragStart(event) {
      if (!this.thumbContainer || !this.desktopMql.matches) return;
      if (event.button !== 0) return;

      this.isThumbDragging = true;
      this.thumbDragMoved = false;
      this.thumbDragStartX = event.clientX;
      this.thumbDragStartScrollLeft = this.thumbContainer.scrollLeft;
      this.thumbContainer.classList.add('is-dragging');

      window.addEventListener('mousemove', this.handleThumbDragMove);
      window.addEventListener('mouseup', this.handleThumbDragEnd);
    }

    handleThumbDragMove(event) {
      if (!this.isThumbDragging || !this.thumbContainer) return;

      const deltaX = event.clientX - this.thumbDragStartX;
      if (Math.abs(deltaX) > 4) {
        this.thumbDragMoved = true;
      }

      if (this.thumbDragMoved) {
        event.preventDefault();
      }

      this.thumbContainer.scrollLeft = this.thumbDragStartScrollLeft - deltaX;
    }

    handleThumbDragEnd() {
      if (!this.isThumbDragging || !this.thumbContainer) return;

      this.isThumbDragging = false;
      this.thumbContainer.classList.remove('is-dragging');
      window.removeEventListener('mousemove', this.handleThumbDragMove);
      window.removeEventListener('mouseup', this.handleThumbDragEnd);

      window.setTimeout(() => {
        this.thumbDragMoved = false;
      }, 0);
    }

    handleThumbNativeDragStart(event) {
      if (!this.desktopMql.matches) return;
      event.preventDefault();
    }

    scrollThumbsBy(direction) {
      if (!this.thumbContainer || !this.desktopMql.matches) return;

      const firstThumb = this.thumbContainer.querySelector('[data-pdp-thumb-btn]');
      const thumbWidth = firstThumb ? firstThumb.getBoundingClientRect().width : 80;
      const styles = firstThumb ? window.getComputedStyle(firstThumb) : null;
      const gap = styles ? parseFloat(styles.marginRight || '0') : 0;
      const step = Math.max(this.thumbContainer.clientWidth - thumbWidth, (thumbWidth + gap) * 4);

      this.thumbContainer.scrollBy({
        left: step * direction,
        behavior: 'smooth',
      });
    }

    updateThumbNavState() {
      if (!this.thumbPrevButton || !this.thumbNextButton || !this.thumbContainer) return;

      if (!this.desktopMql.matches) {
        this.thumbPrevButton.disabled = true;
        this.thumbNextButton.disabled = true;
        return;
      }

      const maxScrollLeft = Math.max(0, this.thumbContainer.scrollWidth - this.thumbContainer.clientWidth);
      const scrollLeft = this.thumbContainer.scrollLeft;
      const hasOverflow = maxScrollLeft > 4;

      this.thumbPrevButton.disabled = !hasOverflow || scrollLeft <= 4;
      this.thumbNextButton.disabled = !hasOverflow || scrollLeft >= maxScrollLeft - 4;
    }

    syncMediaGallery(mediaId) {
      if (!mediaId) {
        warn(`Cannot sync hidden media gallery for section ${this.sectionId}: missing media id.`);
        return;
      }

      if (!this.mediaGallery || typeof this.mediaGallery.setActiveMedia !== 'function') {
        warn(`Hidden media gallery sync skipped for section ${this.sectionId}: setActiveMedia unavailable.`);
        return;
      }

      const targetMediaId = `${this.sectionId}-${mediaId}`;
      log(`Syncing hidden media gallery for section ${this.sectionId}`, { targetMediaId });
      this.mediaGallery.setActiveMedia(targetMediaId, false);
    }

    setZoomSource() {
      const zoomSrc = this.mainImage.getAttribute('data-zoom-src') || this.mainImage.currentSrc || this.mainImage.src;

      if (!zoomSrc) {
        warn(`Zoom source missing for gallery ${this.sectionId}; pane cannot render.`);
        return;
      }

      if (zoomSrc === this.currentZoomSrc && this.zoomImageReady) {
        log(`Zoom source already ready for gallery ${this.sectionId}`, { zoomSrc });
        return;
      }

      this.currentZoomSrc = zoomSrc;
      this.zoomImageReady = false;
      this.pane.style.backgroundImage = `url("${zoomSrc}")`;

      log(`Loading zoom image for gallery ${this.sectionId}`, { zoomSrc });
      this.zoomImage.onload = () => {
        this.zoomImageReady = true;
        log(`Zoom image loaded for gallery ${this.sectionId}`, {
          zoomSrc,
          naturalWidth: this.zoomImage.naturalWidth,
          naturalHeight: this.zoomImage.naturalHeight,
        });

        if (this.lastPointerEvent) {
          this.updateZoom(this.lastPointerEvent);
        }
      };

      this.zoomImage.onerror = () => {
        this.zoomImageReady = false;
        warn(`Zoom image failed to load for gallery ${this.sectionId}`, { zoomSrc });
      };

      this.zoomImage.src = zoomSrc;

      if (this.zoomImage.complete && this.zoomImage.naturalWidth) {
        this.zoomImageReady = true;
        log(`Zoom image already cached for gallery ${this.sectionId}`, { zoomSrc });
      }
    }

    handleMouseEnter(event) {
      log(`Mouse entered gallery ${this.sectionId}`);
      this.updateZoom(event);
    }

    handleMouseMove(event) {
      const now = Date.now();
      if (now - this.lastMoveLogAt >= MOVE_LOG_INTERVAL) {
        this.lastMoveLogAt = now;
        log(`Mouse move in gallery ${this.sectionId}`, {
          x: event.clientX,
          y: event.clientY,
        });
      }

      this.updateZoom(event);
    }

    handleMouseLeave() {
      this.resetZoom('mouse left image area');
    }

    updateZoom(event) {
      if (!this.desktopMql.matches) {
        warn(`Zoom skipped for gallery ${this.sectionId}: viewport is below desktop threshold.`);
        this.resetZoom('non-desktop viewport');
        return;
      }

      if (!this.mainImage.complete || !this.mainImage.naturalWidth || !this.mainImage.naturalHeight) {
        warn(`Zoom skipped for gallery ${this.sectionId}: main image not fully loaded yet.`);
        return;
      }

      const imageRect = this.mainImage.getBoundingClientRect();
      const offsetX = event.clientX - imageRect.left;
      const offsetY = event.clientY - imageRect.top;

      if (offsetX < 0 || offsetY < 0 || offsetX > imageRect.width || offsetY > imageRect.height) {
        warn(`Zoom pane hidden for gallery ${this.sectionId}: pointer moved outside image bounds.`, {
          offsetX,
          offsetY,
          width: imageRect.width,
          height: imageRect.height,
        });
        this.resetZoom('pointer outside image bounds');
        return;
      }

      this.lastPointerEvent = event;
      this.setZoomSource();

      const naturalWidth = this.zoomImageReady ? this.zoomImage.naturalWidth : this.mainImage.naturalWidth;
      const naturalHeight = this.zoomImageReady ? this.zoomImage.naturalHeight : this.mainImage.naturalHeight;

      if (!naturalWidth || !naturalHeight) {
        warn(`Zoom pane hidden for gallery ${this.sectionId}: no natural image dimensions available.`);
        return;
      }

      const scaleX = (naturalWidth / imageRect.width) * ZOOM_BOOST;
      const scaleY = (naturalHeight / imageRect.height) * ZOOM_BOOST;
      const lensWidth = Math.max(70, Math.min(imageRect.width, PANE_SIZE / scaleX));
      const lensHeight = Math.max(70, Math.min(imageRect.height, PANE_SIZE / scaleY));
      const lensLeft = Math.min(Math.max(offsetX - lensWidth / 2, 0), imageRect.width - lensWidth);
      const lensTop = Math.min(Math.max(offsetY - lensHeight / 2, 0), imageRect.height - lensHeight);

      this.positionPane(imageRect);
      this.root.classList.add('is-zooming');
      this.mainArea.classList.add('is-zooming');
      this.lens.style.width = `${lensWidth}px`;
      this.lens.style.height = `${lensHeight}px`;
      this.lens.style.left = `${this.mainImage.offsetLeft + lensLeft}px`;
      this.lens.style.top = `${this.mainImage.offsetTop + lensTop}px`;
      this.lens.style.opacity = '1';
      this.lens.style.visibility = 'visible';
      this.pane.classList.add('is-visible');
      this.pane.style.setProperty('display', 'block', 'important');
      this.pane.style.setProperty('opacity', '1', 'important');
      this.pane.style.setProperty('visibility', 'visible', 'important');
      this.pane.style.backgroundSize = `${naturalWidth * ZOOM_BOOST}px ${naturalHeight * ZOOM_BOOST}px`;
      this.pane.style.backgroundPosition = `${-lensLeft * scaleX}px ${-lensTop * scaleY}px`;

      log(`Zoom pane visible for gallery ${this.sectionId}`, {
        lensLeft,
        lensTop,
        lensWidth,
        lensHeight,
        backgroundPosition: this.pane.style.backgroundPosition,
      });
    }

    positionPane(imageRect) {
      const rightSpace = window.innerWidth - imageRect.right - PANE_GUTTER;
      const leftSpace = imageRect.left - PANE_GUTTER;
      const placeRight = rightSpace >= PANE_SIZE || rightSpace >= leftSpace;
      const paneLeft = placeRight
        ? imageRect.right + PANE_GUTTER
        : imageRect.left - PANE_GUTTER - PANE_SIZE;
      const paneTop = Math.max(12, Math.min(imageRect.top, window.innerHeight - PANE_SIZE - 12));

      this.pane.style.left = `${paneLeft}px`;
      this.pane.style.top = `${paneTop}px`;
      this.pane.dataset.panePlacement = placeRight ? 'right' : 'left';

      log(`Pane positioned for gallery ${this.sectionId}`, {
        placement: this.pane.dataset.panePlacement,
        paneLeft,
        paneTop,
        rightSpace,
        leftSpace,
      });
    }

    activateMediaById(mediaId) {
      if (!mediaId) {
        warn(`Variant sync skipped for gallery ${this.sectionId}: missing media id.`);
        return;
      }

      const thumb = this.root.querySelector(`[data-pdp-thumb-btn][data-media-id="${mediaId}"]`);
      if (!thumb) {
        warn(`Variant sync could not find matching thumbnail in gallery ${this.sectionId}`, { mediaId });
        return;
      }

      log(`Activating media from variant change in gallery ${this.sectionId}`, { mediaId });
      thumb.click();
    }

    resetZoom(reason) {
      log(`Resetting zoom for gallery ${this.sectionId}: ${reason}`);
      this.lastPointerEvent = null;
      this.root.classList.remove('is-zooming');
      this.mainArea.classList.remove('is-zooming');
      this.lens.style.opacity = '0';
      this.lens.style.visibility = 'hidden';
      this.pane.classList.remove('is-visible');
      this.pane.style.setProperty('display', 'none', 'important');
      this.pane.style.setProperty('opacity', '0', 'important');
      this.pane.style.setProperty('visibility', 'hidden', 'important');
    }

    destroy(reason) {
      log(`Destroying gallery ${this.sectionId}: ${reason}`);
      this.mainArea.removeEventListener('mouseenter', this.handleMouseEnter);
      this.mainArea.removeEventListener('mousemove', this.handleMouseMove);
      this.mainArea.removeEventListener('mouseleave', this.handleMouseLeave);
      this.mainImage.removeEventListener('load', this.handleMainImageLoad);
      this.root.removeEventListener('click', this.handleThumbClick);
      this.thumbPrevButton?.removeEventListener('click', this.handleThumbPrevClick);
      this.thumbNextButton?.removeEventListener('click', this.handleThumbNextClick);
      this.thumbContainer?.removeEventListener('scroll', this.handleThumbScroll, { passive: true });
      this.thumbContainer?.removeEventListener('mousedown', this.handleThumbDragStart);
      this.thumbContainer?.removeEventListener('dragstart', this.handleThumbNativeDragStart);
      window.removeEventListener('mousemove', this.handleThumbDragMove);
      window.removeEventListener('mouseup', this.handleThumbDragEnd);

      if (typeof this.desktopMql.removeEventListener === 'function') {
        this.desktopMql.removeEventListener('change', this.handleViewportChange);
      } else if (typeof this.desktopMql.removeListener === 'function') {
        this.desktopMql.removeListener(this.handleViewportChange);
      }

      this.attributeObserver?.disconnect();
      this.galleryObserver?.disconnect();
      this.resetZoom('controller destroy');
      this.pane.remove();
    }
  }

  function initGallery(root) {
    if (!root) return;

    const existing = controllers.get(root);
    if (existing) {
      existing.destroy('reinitialization');
      controllers.delete(root);
    }

    const controller = new PdpZoomController(root);
    if (!controller.mainArea || !controller.mainImage || !controller.lens || !controller.pane) return;
    controllers.set(root, controller);
  }

  function initAll(scope) {
    const context = scope && scope.querySelectorAll ? scope : document;
    const galleries = [];

    if (context.matches && context.matches('[data-pdp-gallery]')) {
      galleries.push(context);
    }

    galleries.push(...context.querySelectorAll('[data-pdp-gallery]'));
    log('Scanning for PDP galleries', { count: galleries.length });
    galleries.forEach((gallery) => initGallery(gallery));
  }

  function bindVariantSubscription() {
    if (variantSubscriptionBound) return;

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      warn('Variant subscription unavailable: pubsub globals not found.');
      return;
    }

    subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      const sectionId = payload?.data?.sectionId;
      const mediaId = payload?.data?.variant?.featured_media?.id;
      log('Variant change received', { sectionId, mediaId });

      controllers.forEach((controller) => {
        if (controller.sectionId !== sectionId) return;
        controller.activateMediaById(String(mediaId || ''));
      });
    });

    variantSubscriptionBound = true;
    log('Variant subscription attached');
  }

  function boot() {
    initAll(document);
    bindVariantSubscription();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    log('Shopify section loaded', event.detail);
    initAll(event.target || document);
    bindVariantSubscription();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = String(event.detail?.sectionId || '');
    log('Shopify section unloading', { sectionId });

    Array.from(controllers.entries()).forEach(([root, controller]) => {
      if (controller.sectionId !== sectionId) return;
      controller.destroy('shopify section unload');
      controllers.delete(root);
    });
  });

  document.addEventListener('product-info:loaded', (event) => {
    log('product-info:loaded received', event.target);
    const sectionRoot = event.target?.closest?.('[data-section]');
    if (!sectionRoot) {
      warn('product-info:loaded did not provide a section root for zoom reinit.');
      return;
    }

    initAll(sectionRoot);
    bindVariantSubscription();
  });

  window.initPdpZoomGalleries = initAll;
})();
